import axios from 'axios';
import { removeUser } from '../../redux/auth';
import { store } from '../../redux/store';


export const baseURL = process.env.REACT_APP_API_URL;
// export const baseURL = 'https://api.getdante.io';
// export const baseURL = 'https://6d2d-178-216-209-232.ngrok-free.app';

// JWT парсинг
function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
}

// Проверка истечения access-токена (с запасом 60 сек).
// Использует клиентские часы — при расхождении это лишь чаще инициирует
// проактивный refresh (безвредно), но не приводит к разлогину.
function isTokenExpired(token) {
  const parsed = parseJwt(token);
  if (!parsed?.exp) return true;
  return Date.now() > (parsed.exp * 1000) - 60000;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// Классификация ошибок refresh (сверено с бэком: session/controllers.py).
//
// Refresh-токен НЕ ротируется, но сессии хранятся в DynamoDB и сервер их удаляет
// (logout с другого устройства, действие админа, антифрод); refresh без живой
// сессии отклоняется. Эндпоинт отдаёт только 400/403/404/503 и НИКОГДА 401.
//
// Сообщения локализованы (ru по умолчанию, в kg-словаре — пустые строки),
// поэтому матчить по тексту нельзя — классифицируем строго по HTTP-статусу:
//   нет ответа (сеть/таймаут)      -> RETRY  (оставляем сессию, ретраим)
//   400 / 401 / 403                -> DEAD   (мёртвый refresh / нет сессии / деактивация)
//   всё остальное (404, 5xx, ...)  -> RETRY
// ---------------------------------------------------------------------------
// 'DEAD'  -> разлогин: clearAuth + редирект на /login
// 'RETRY' -> временная ошибка: оставить сессию, повторить с бэкоффом
function classifyRefreshError(error) {
  const response = error?.response;

  // Нет ответа вовсе — сеть/таймаут/CORS -> временное
  if (!response) return 'RETRY';

  const status = response.status;

  // 400 — мёртвый/битый refresh или удалённая сессия; 403 — деактивация.
  // 401 бэк не шлёт, но оставляем как часть правила по статусам.
  if (status === 400 || status === 401 || status === 403) return 'DEAD';

  // 404, 5xx и прочее — временное, ретраим.
  return 'RETRY';
}

function clearAuth() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  store.dispatch(removeUser());
}

// Отдельный axios-инстанс БЕЗ интерцепторов — чтобы refresh не рекурсировал
// через интерцепторы приложения.
const bareAxios = axios.create({ baseURL, timeout: 15000 });

// Один запрос обновления с ретраями и бэкоффом.
// Разлогинивает ТОЛЬКО при классе 'DEAD'. При исчерпании ретраев на 'RETRY'
// пробрасывает ошибку (сессия сохраняется, localStorage не трогается).
async function performRefresh() {
  const refreshTokenValue = localStorage.getItem('refreshToken');
  if (!refreshTokenValue) {
    // Нечем обновляться — это DEAD.
    clearAuth();
    throw new Error('No refresh token');
  }

  const maxAttempts = 3;
  let lastError;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const response = await bareAxios.post(
        '/session/refresh',
        {},
        { headers: { 'X-Refresh-Token': refreshTokenValue } }
      );

      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('refreshToken', response.data.refresh_token);
      return response.data.access_token;
    } catch (error) {
      lastError = error;
      const kind = classifyRefreshError(error);

      if (kind === 'DEAD') {
        console.error('[Auth] Refresh token is dead, logging out', {
          status: error.response?.status,
          message: error.response?.data?.message || error.message,
        });
        clearAuth();
        throw error;
      }

      // RETRY: бэкофф 300ms -> 600ms -> ... с джиттером, сессию не трогаем
      if (attempt < maxAttempts - 1) {
        const backoff = 300 * 2 ** attempt + Math.floor(Math.random() * 150);
        console.warn('[Auth] Refresh transient failure, retrying', {
          attempt: attempt + 1,
          status: error.response?.status,
          message: error.response?.data?.message || error.message,
          backoff,
        });
        await sleep(backoff);
      }
    }
  }

  // Ретраи исчерпаны, но это НЕ смерть токена — сессию сохраняем.
  console.warn('[Auth] Refresh retries exhausted, keeping session', {
    message: lastError?.response?.data?.message || lastError?.message,
  });
  throw lastError;
}

// ---------------------------------------------------------------------------
// Single-flight: и в рамках вкладки, и между вкладками.
//   - in-tab: общий промис refreshPromise, переиспользуется request- и
//     response(401)-интерцепторами.
//   - cross-tab: Web Locks API — только одна вкладка делает refresh, остальные
//     ждут и перечитывают токен из localStorage. Fallback — локк через
//     localStorage с таймаутом.
// ---------------------------------------------------------------------------
let refreshPromise = null;

const LOCK_NAME = 'dante-token-refresh';
const LS_LOCK_KEY = 'refreshLock';
const LS_LOCK_TTL = 15000; // мс — защита от «залипшего» локка

async function withCrossTabLock(fn) {
  // Предпочтительно — Web Locks API
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(LOCK_NAME, fn);
  }

  // Fallback: локк через localStorage с коротким поллингом.
  const tokenBefore = localStorage.getItem('token');
  const start = Date.now();
  // Пытаемся захватить локк
  while (true) {
    const raw = localStorage.getItem(LS_LOCK_KEY);
    const lockedAt = raw ? Number(raw) : 0;
    const stale = !lockedAt || Date.now() - lockedAt > LS_LOCK_TTL;

    if (stale) {
      localStorage.setItem(LS_LOCK_KEY, String(Date.now()));
      try {
        return await fn();
      } finally {
        localStorage.removeItem(LS_LOCK_KEY);
      }
    }

    // Другая вкладка уже обновляет — ждём и проверяем, не появился ли новый токен
    await sleep(200);
    const tokenNow = localStorage.getItem('token');
    if (tokenNow && tokenNow !== tokenBefore) {
      return tokenNow; // другая вкладка обновила токен
    }
    if (Date.now() - start > LS_LOCK_TTL) {
      // Локк завис — форсируем собственный refresh
      localStorage.setItem(LS_LOCK_KEY, String(Date.now()));
      try {
        return await fn();
      } finally {
        localStorage.removeItem(LS_LOCK_KEY);
      }
    }
  }
}

// Точка входа: гарантирует единственный одновременный refresh.
function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = withCrossTabLock(async () => {
    // Внутри локка ещё раз проверяем: вдруг другая вкладка/поток уже обновили
    // токен, пока мы ждали, — тогда refresh не нужен.
    const current = localStorage.getItem('token');
    if (current && !isTokenExpired(current)) {
      return current;
    }
    return performRefresh();
  })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

const api = axios.create({
  baseURL,
});

api.interceptors.request.use(async (ctx) => {
  const accessToken = localStorage.getItem('token');

  if (accessToken && isTokenExpired(accessToken)) {
    try {
      const newToken = await refreshAccessToken();
      ctx.headers['X-Token'] = newToken;
    } catch (error) {
      // Если это была временная ошибка — сессия сохранена, но текущий запрос
      // не можем авторизовать; отклоняем именно его.
      return Promise.reject(error);
    }
  } else if (accessToken) {
    ctx.headers['X-Token'] = accessToken;
  }

  return ctx;
});

api.interceptors.response.use(
  function (config) {
    return config;
  },
  async function (error) {
    const { response, config: originalRequest } = error;

    // Один retry на 401 через общий single-flight refresh.
    if (response?.status === 401 && originalRequest && !originalRequest._retried) {
      originalRequest._retried = true;
      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers['X-Token'] = newToken;
        return api(originalRequest);
      } catch (refreshError) {
        // performRefresh уже сделал clearAuth() только если токен реально мёртв.
        // Иначе — сессия сохранена, просто пробрасываем ошибку запроса.
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
